import {
    AllEvents,
    ConfigurationContext,
    DataLayerEventName,
    ElectionDefinition,
    queries,
} from '@news-mono/web-common'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
} from 'react'
import { useImpressionAvailable } from '../../../__helpers/impression-available-helper'
import { CustomPageSharing } from '../../../buttons/SharingCustomPage/CustomPageSharing'
import { ElectionDataFetchError, ElectionFilter } from '../components'
import { getPartyColors } from '../data'
import { ElectionTppEstCard } from '../ElectionTppEstCard/ElectionTppEstCard'
import { TheSeatsDivider } from '../TheSeatsWidget/TheSeatsWidget.styled'
import {
    ElectionTppEstCardLoadingPlaceholder,
    PartyTotalsContainer,
    PartyTotalsDescriptionContainer,
    PartyTotalsHeadingContainer,
    PartyTotalsTable,
    PartyTotalsTableData,
    PartyTotalsWidgetContainer,
    TableDataLoadingPlaceholder,
    TableHeadingLoadingPlaceholder,
} from './PartyTotals.styled'
import { ElectionPredictionDisclaimer } from '../components/ElectionPredictionDisclaimer'
import { ElectionFilterContext } from '../../../contexts'
import { useTheme } from '@emotion/react'
import {
    getAllFilteredElectionParties,
    getAreaForElection,
} from '../data/election-data'
import { electionsDebug } from '../../../__helpers/elections-debug'
import { LoggerContext } from '../../../diagnostics/LoggerContext'
import {
    ElectionConfig,
    PartyDto,
} from '@west-australian-newspapers/election-api-types'
import { useProduct } from '../../../__product'

type PartyTotalsProps = {
    onEvent: (event: AllEvents) => void
    electionDefinition: ElectionDefinition
    description: string
    isTppWidgetEnabled: boolean
    colorMode?: 'light' | 'dark'
}

export const PartyTotalsComponent = ({
    onEvent,
    electionDefinition,
    description,
    isTppWidgetEnabled,
    colorMode,
}: PartyTotalsProps) => {
    const theme = useTheme()
    const { state: stateFilter } = useContext(ElectionFilterContext)

    const config = useContext(ConfigurationContext)
    const logger = useContext(LoggerContext)
    const product = useProduct()
    const result = useQuery(
        queries['election-api'].definition({
            initialDefinition: electionDefinition,
            electionAPI: config.electionApi,
            caller: config.apiCallerHeader,
        }),
    )

    const electionConfig =
        result.isSuccess && result.data.electionData.data
            ? result.data?.electionData.config
            : undefined

    const transformedData =
        result.isSuccess && result.data.electionData.data
            ? result.data?.electionData.data
            : undefined

    // Filter and sort the party data
    const { parties: partyData, pctCounted: votesCountedPercentage } =
        useMemo(() => {
            if (!transformedData || !electionConfig) {
                return { parties: undefined, pctCounted: undefined }
            }
            return getAllFilteredElectionParties(
                transformedData,
                stateFilter.value,
                electionConfig,
            )
        }, [electionConfig, stateFilter.value, transformedData])

    let votesCountedText = null

    if (
        votesCountedPercentage !== undefined &&
        votesCountedPercentage !== null
    ) {
        if (votesCountedPercentage === 0) {
            votesCountedText = '0%'
        } else if (votesCountedPercentage < 1) {
            votesCountedText = '< 1%'
        } else {
            votesCountedText = `${votesCountedPercentage.toFixed(1)}%`
        }
    }

    // Loading state
    if (result.isLoading) {
        return (
            <PartyTotalsWrapper
                headingComponent={<TableHeadingLoadingPlaceholder />}
                beforeHeadingComponent={
                    <ElectionTppEstCardLoadingPlaceholder />
                }
                colorMode={colorMode}
            >
                <PartyTotalsTable colorMode={colorMode}>
                    {getTableHead()}
                    <tbody>
                        {[...Array(3)].map((_, i) => (
                            <TableDataLoadingPlaceholder />
                        ))}
                    </tbody>
                </PartyTotalsTable>
            </PartyTotalsWrapper>
        )
    }

    // Something went wrong, show an error page
    if (
        result.isError ||
        !partyData ||
        partyData.length == 0 ||
        !result.data.electionData.data ||
        !result.data.electionData.config
    ) {
        logger.warn(
            {
                resultError: result.isError,
                partyDataLength: partyData?.length,
                electionDataPresent: !result?.data?.electionData.data,
                electionConfigPresent: !result?.data?.electionData.config,
                electionId: electionDefinition.electionId,
            },
            'PartyTotals election data error',
        )
        return (
            <PartyTotalsWrapper colorMode={colorMode}>
                <ElectionDataFetchError />
            </PartyTotalsWrapper>
        )
    }

    // Show the loaded state
    const { lastModified } = result.data.electionData.data
    const { electionType, seatsTotal } = result.data.electionData.config
    electionsDebug('partyData', partyData)

    return (
        <PartyTotalsWidgetContainer>
            <PartyTotalsDescriptionContainer colorMode={colorMode}>
                <p>{description}</p>
                <CustomPageSharing
                    text={description}
                    shareOptions={[
                        'facebook',
                        'linkedin',
                        'twitter',
                        product === 'thenightly' ? 'email_tn' : 'email',
                        'clipboard',
                    ]}
                    loading={false}
                    onEvent={onEvent}
                />
            </PartyTotalsDescriptionContainer>

            {/* If we're showing the filter buttons, display them :D */}
            {electionType === 'federal' && (
                <ElectionFilter
                    headingText="State"
                    dataLayerEventOriginator="ElectionStateFilter"
                    onEvent={onEvent}
                    dataLayerEventName={DataLayerEventName.searchFilter}
                    electionFilter={stateFilter}
                    hideOnDesktop={theme.kind === 'thenightly'}
                />
            )}
            <TheSeatsDivider hideOnDesktop />
            {isTppWidgetEnabled && (
                <ElectionTppEstCard
                    title="Two-party preferred estimate"
                    data={partyData}
                    config={result.data.electionData.config}
                />
            )}

            <PartyTotalsWrapper
                headingComponent={
                    <p>
                        {votesCountedText !== null
                            ? `${votesCountedText} votes counted - `
                            : ''}
                        updated {format(new Date(lastModified), 'h:mm a')}
                    </p>
                }
                onEvent={onEvent}
                colorMode={colorMode}
            >
                <PartyTotalsTable colorMode={colorMode}>
                    {getTableHead()}
                    <tbody>
                        {filterPartyData(
                            partyData,
                            result.data.electionData.config,
                        ).map((party, i) => {
                            // To fill the graph, a party would have to have the maximum number of seats for the election
                            // i.e. so for the bar to be fill a party would need 100% of the seats (this is how ABC did it 🙈)
                            const seatsAsPercent =
                                (party.seatsWon / seatsTotal) * 100
                            const seatsPredicted = party.seatsPredicted ?? 0
                            const seatsPredictedAsPercent =
                                ((party.seatsWon + seatsPredicted) /
                                    seatsTotal) *
                                100

                            return (
                                <PartyTotalsTableData
                                    key={i}
                                    colors={getPartyColors(party.partyCode)}
                                    seatPercent={Math.min(seatsAsPercent, 100)}
                                    predictedPercent={Math.min(
                                        seatsPredictedAsPercent,
                                        100,
                                    )}
                                >
                                    {getTableData(party)}
                                </PartyTotalsTableData>
                            )
                        })}
                    </tbody>
                </PartyTotalsTable>
            </PartyTotalsWrapper>
            <ElectionPredictionDisclaimer />
        </PartyTotalsWidgetContainer>
    )
}

type PartyTotalsWrapperProps = {
    headingComponent?: React.ReactNode
    beforeHeadingComponent?: React.ReactNode
    onEvent?: (event: AllEvents) => void
    colorMode?: 'light' | 'dark'
}
const PartyTotalsWrapper = ({
    children,
    headingComponent,
    beforeHeadingComponent,
    onEvent,
    colorMode,
}: PropsWithChildren<PartyTotalsWrapperProps>) => {
    const impressionAvailableRef = useImpressionAvailable({
        loading: false,
        available: useCallback(() => {
            onEvent?.({
                type: DataLayerEventName.navAvailable,
                originator: 'ElectionPartyTotalsComponent',
                payload: {
                    navName: 'ElectionPartyTotals',
                },
            })
        }, [onEvent]),
    })

    return (
        <PartyTotalsContainer ref={impressionAvailableRef}>
            {beforeHeadingComponent}
            <PartyTotalsHeadingContainer colorMode={colorMode}>
                <h2>Seats won & seats likely</h2>
                {headingComponent}
            </PartyTotalsHeadingContainer>
            {children}
        </PartyTotalsContainer>
    )
}

/** This function filters out the coalition and other parties for Federal elections */
const filterPartyData = (parties: PartyDto[], config: ElectionConfig) => {
    return config.electionType === 'state'
        ? parties
        : parties.filter(
              (party) => party.partyCode !== 'COA' && party.partyCode !== 'OTH',
          )
}

const getTableHead = () => (
    <thead>
        <tr>
            <th className="partyName"></th>
            <th className="seatsWon">Seats won (+&nbsp;likely)</th>
            <th className="seatsChanged">Seats changed</th>
            <th className="vote">Vote</th>
            <th className="swing">Swing</th>
        </tr>
    </thead>
)

const getTableData = (party: PartyDto) => {
    const seatsPredicted = party.seatsPredicted ?? 0

    const { seatsGained, seatsLost, seatsChanged } = party
    let seatsChangedCount = seatsChanged

    // calculate seats changed via the gained and lost seats
    // these numbers should exist permanently now so can remove zod optional type
    if (seatsGained !== undefined && seatsLost !== undefined) {
        seatsChangedCount = seatsGained - seatsLost
    }

    return (
        <>
            <td className="partyName">{party.partyName}</td>
            <td className="seatsWon">
                {party.seatsWon} (+{seatsPredicted})
            </td>
            <td className="seatsChanged">{seatsChangedCount}</td>
            <td className="vote">
                <div>{party.votePct.toFixed(1)}%</div>
                <div className="voteCount">
                    {/* Intl.NumberFormat adds the commas as per the design */}
                    {Intl.NumberFormat('en-AU').format(party.voteCount)}
                </div>
            </td>
            <td className="swing">
                {/* If the Swing Pct is null, fallback to a dash, was decided previously iirc */}
                {party.swingPct ? `${party.swingPct.toFixed(1)}%` : '-'}
            </td>
        </>
    )
}
